import { FormGroup, Validators } from '@angular/forms';

import { FieldValidatorsMap, FormValidatorsMap } from '../interfaces';
import { UserValidationAction } from './user-validation-action.enum';

import { ObjectLike } from 'app/interfaces';
import { FormModel } from 'app/models';
import { disallowContent, hashValidator, isIban, matching, updateValidity } from 'app/validators';

export class UserRules extends FormModel<UserValidationAction> {
    formValidators: FormValidatorsMap = {
        [UserValidationAction.AcceptTerms]: {
            acceptsPrivacy: [Validators.requiredTrue],
            acceptsTerms: [Validators.requiredTrue],
        },
        [UserValidationAction.CampaignForm]: {
            acceptsRules: [Validators.requiredTrue],
            email: [Validators.required],
        },
        [UserValidationAction.Registration]: {
            acceptsPrivacy: [Validators.requiredTrue],
            acceptsTerms: [Validators.requiredTrue],
            email: [Validators.required],
            password: [Validators.required, Validators.minLength(8)],
        },
        [UserValidationAction.SecuritySettings]: {
            password: [Validators.required],
        },
        [UserValidationAction.Reset]: {
            email: [Validators.required],
            newPassword: [updateValidity(['repeatPassword'])],
            repeatPassword: [matching('newPassword')],
        },
        [UserValidationAction.UsergroupStore]: {
            email: [Validators.required],
            firstName: [Validators.required],
            lastName: [Validators.required],
            pin: [Validators.required],
        },
        [UserValidationAction.UsergroupUpdate]: {
            email: [Validators.required],
        },
        [UserValidationAction.UsergroupEmployeeUpdate]: {
            email: [Validators.required],
            firstName: [Validators.required],
            lastName: [Validators.required],
        },
        [UserValidationAction.UsergroupTaxExeptions]: {
            taxException: [Validators.required],
        },
    };
    baseValidators: FieldValidatorsMap = {
        acceptsPrivacy: [],
        acceptsRules: [],
        acceptsTerms: [],
        address: [],
        birthDate: [],
        city: [],
        companyName: [Validators.minLength(2), Validators.required],
        country: [],
        currentPassword: [Validators.required],
        distance: [],
        duration: [],
        email: [Validators.email],
        firstName: [],
        userHash: [
            Validators.required,
            disallowContent(new RegExp('[^a-zA-Z0-9_.]+'), 'error.validation.user_hash_allowed_content'),
        ],
        usergroupHash: [Validators.required, hashValidator()],
        height: [Validators.min(0)],
        iban: [isIban()],
        language: [],
        lastName: [],
        newPassword: [Validators.required, Validators.minLength(8)],
        password: [],
        phone: [],
        pin: [],
        redeemCode: [Validators.required],
        repeatPassword: [Validators.required],
        role: [],
        sendInvoiceByEmail: [],
        stepLength: [Validators.min(0)],
        taxException: [],
        vatNr: [],
        weight: [Validators.min(0)],
    };

    getAcceptTermsForm(initialValues: ObjectLike = {}) {
        return this.createFormGroup(UserValidationAction.AcceptTerms, initialValues);
    }

    getCampaignForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.CampaignForm, initialValues);
    }

    getContactSettingsForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.ContactSettings, initialValues);
    }

    getGeneralSettingsForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.GeneralSettings, initialValues);
    }

    getGeneralSettingsHashForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.GeneralSettingsHash, initialValues);
    }

    getPhysicalSettingsForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.PhysicalSettings, initialValues);
    }

    getRegistrationForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.Registration, initialValues);
    }

    getResetForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.Reset, initialValues);
    }

    getSecurityForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.SecuritySettings, initialValues);
    }

    getUsergroupStoreForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.UsergroupStore, initialValues);
    }

    getUsergroupUpdateForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.UsergroupUpdate, initialValues);
    }

    getUsergroupUpdateEmployeeForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.UsergroupEmployeeUpdate, initialValues);
    }

    getUsergroupUpdateHashForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.UsergroupUpdateHash, initialValues);
    }

    getUsergroupExceptionsForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.UsergroupExceptions, initialValues);
    }

    getUsergroupTaxExceptionForm(initialValues: ObjectLike): FormGroup {
        return this.createFormGroup(UserValidationAction.UsergroupTaxExeptions, initialValues);
    }

    getUserRedeemForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.UserRedeem, initialValues);
    }

    getUserUpdateForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.UserUpdate, initialValues);
    }

    getLoginForm(initialValues: ObjectLike = {}): FormGroup {
        return this.createFormGroup(UserValidationAction.Login, initialValues);
    }
}
